import React, { useEffect, useState } from 'react';
import { GridRenderCellParams } from '@mui/x-data-grid';
import { Icon, IconButton, ListItemIcon, Menu, MenuItem, Snackbar, Tooltip } from '@mui/material';

import { PortfolioOrganization, PortfolioPlan } from '../../../models/portfolio/portfolio';

import ProgressBar from '../../controls/ProgressBar';
import ErrorMessage from '../../controls/ErrorMessage';

import { portfolioService } from '../../../services/portfolio.service';
import { eventBus } from '../../../services/event-emitter.service';
import { authService } from '../../../services/auth.service';

import DialogConfirm from '../../controls/dialogs/DialogConfirm';
import DialogConfirmSelectable, { DialogSelectableItem } from '../../controls/dialogs/DialogConfirmSelectable';


interface OrganisatiesActionMenuProps {
  params: GridRenderCellParams<PortfolioOrganization>;
  portfolioId: string;
  mijnOrganisatieId?: string;
  eigenOrganisatieActief?: boolean;
  canCrud?: boolean;
  plannen?: PortfolioPlan[];
}

const RELOAD_PORTFOLIO_DELAY_MS = 2000;

const OrganisatiesActionMenu = ({
  params,
  portfolioId,
  mijnOrganisatieId,
  eigenOrganisatieActief,
  canCrud,
  plannen,
}: OrganisatiesActionMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentRow, setCurrentRow] = useState<PortfolioOrganization | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [open, setOpenDialogs] = useState({
    snackbar: false,
    confirm: false,
  });

  const [pendingAction, setPendingAction] = useState<{
    status: 'accepted' | 'declined' | 'cancelled' | 'left';
    message: string;
    confirmTitle: string;
    confirmContent: string;
    confirmButtonText: string;
  } | null>(null);

  useEffect(() => {
    setCurrentRow(params.row);
  }, [params]);

  const openDialog = (dialog: keyof typeof open) => {
    setOpenDialogs((prev) => ({ ...prev, [dialog]: true }));
  };

  const closeDialog = (dialog: keyof typeof open) => {
    setOpenDialogs((prev) => ({ ...prev, [dialog]: false }));
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const isEigenOrganisatie = !!currentRow && currentRow.organizationId === mijnOrganisatieId;

  const uitgenodigdePlannen = (plannen ?? []).filter(
    (plan) =>
      !!currentRow &&
      (
        plan.organizationId === currentRow.organizationId ||
        plan.infplOrganizationId === currentRow.organizationId ||
        (isEigenOrganisatie && (plan.organizationId === mijnOrganisatieId || plan.infplOrganizationId === mijnOrganisatieId))
      ) &&
      (plan.inclusionRequestStatus ?? '').toLowerCase() === 'pending' &&
      !!plan.inclusionRequestId
  );

  const uitgenodigdePlanItems: DialogSelectableItem[] = uitgenodigdePlannen.map((plan) => ({
    id: plan.inclusionRequestId as string,
    label: plan.planNummer ? `${plan.planNaam ?? 'Onbekend plan'} (${plan.planNummer})` : (plan.planNaam ?? 'Onbekend plan'),
  }));

  const heeftUitgenodigdePlannen = uitgenodigdePlanItems.length > 0;

  const onClickUpdateStatus = (status: 'accepted' | 'declined' | 'cancelled' | 'left') => {
    if (!currentRow) return;

    let message = '';
    let confirmTitle = '';
    let confirmContent = '';
    let confirmButtonText = '';

    switch (status) {
      case 'accepted':
        message = 'Uitnodiging voor het afstemmingsdossier is geaccepteerd';
        confirmTitle = 'Uitnodiging accepteren';
        confirmContent = `Weet je zeker dat je de uitnodiging voor ${currentRow.displayName || 'deze organisatie'} voor dit afstemmingsdossier wilt accepteren?`;
        confirmButtonText = 'Ja, ik wil de uitnodiging accepteren';
        break;

      case 'declined':
        message = 'Uitnodiging voor het afstemmingsdossier is geweigerd';
        confirmTitle = 'Uitnodiging weigeren';
        confirmContent = `Weet je zeker dat je de uitnodiging voor ${currentRow.displayName || 'deze organisatie'} voor dit afstemmingsdossier wilt weigeren?`;
        confirmButtonText = 'Ja, ik wil de uitnodiging weigeren';
        break;

      case 'left':
        message = 'Je organisatie is gestopt met deelname aan het afstemmingsdossier';
        confirmTitle = 'Stop deelname';
        confirmContent = `Weet je zeker dat ${currentRow.displayName || 'deze organisatie'} wil stoppen met deelname aan dit afstemmingsdossier? Al je plannen in dit dossier worden geannuleerd.`;
        confirmButtonText = 'Ja, ik wil stoppen met deelname';
        break;

      case 'cancelled':
        message = 'De uitnodiging voor de organisatie is geannuleerd';
        confirmTitle = 'Uitnodiging annuleren';
        confirmContent = `Weet je zeker dat je de uitnodiging voor ${currentRow.displayName || 'deze organisatie'} wilt annuleren?`;
        confirmButtonText = 'Ja, ik wil de uitnodiging annuleren';
        break;

      default:
        return;
    }

    setPendingAction({
      status,
      message,
      confirmTitle,
      confirmContent,
      confirmButtonText,
    });

    openDialog('confirm');
    handleCloseMenu();
  };

  const handleConfirm = (selectedPlanIds: string[] = []) => {
    if (pendingAction) {
      updateStatus(pendingAction.status, pendingAction.message, selectedPlanIds);
    }

    closeDialog('confirm');
    setTimeout(() => {
      setPendingAction(null);
    }, 100);
  };

  const updateStatus = async (
    status: 'accepted' | 'declined' | 'cancelled' | 'left',
    message: string,
    selectedPlanIds: string[] = []
  ) => {
    if (!currentRow) return;

    try {
      setLoading(true);

      let response;
      if (status === 'accepted' || status === 'declined') {
        response = await portfolioService.replyToOrganizationInclusion(portfolioId, status);
      }
      else if (status === 'left') {
        response = await portfolioService.leavePortfolioAsOrganization(portfolioId);
      }
      else if (status === 'cancelled') {
        response = await portfolioService.cancelOrganizationInvitation(portfolioId, currentRow.organizationId);
      }

      if (response && response.ok === false) {
        setLoading(false);
        throw new Error('Verzoek is mislukt');
      }

      if (status === 'accepted' && selectedPlanIds.length > 0) {
        await Promise.all(
          selectedPlanIds.map((inclusionRequestId) =>
            portfolioService.updatePlanDeelnameStatus('accepted', inclusionRequestId)
          )
        );
      }

      setSnackbarMessage(message);
      openDialog('snackbar');

      setTimeout(() => {
        setLoading(false);
        eventBus.emit('reloadPortfolio', status);
      }, RELOAD_PORTFOLIO_DELAY_MS);
    }
    catch (error) {
      setLoading(false);
      console.error('Error updating organisatie inclusion status:', error);
      setError('Er is een fout opgetreden');
    }
  };

  if (loading) {
    return <ProgressBar />;
  }

  if (error) {
    return <ErrorMessage message={error} />;
  }

  const showEigenPending = isEigenOrganisatie && currentRow?.status === 'pending' && authService.canCrud() && canCrud;
  const showEigenAccepted = isEigenOrganisatie && currentRow?.status === 'accepted' && authService.canCrud() && canCrud;
  const showCancel = !isEigenOrganisatie && currentRow?.status === 'pending' && eigenOrganisatieActief && authService.canCrud() && canCrud;

  const hasActions = showEigenPending || showEigenAccepted || showCancel;

  if (!hasActions) {
    return <></>;
  }

  return (
    <>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={open.snackbar}
        autoHideDuration={2000}
        onClose={() => closeDialog('snackbar')}
        message={snackbarMessage ? snackbarMessage : 'Er is een fout opgetreden'}
        key="topcenter"
      />

      <IconButton onClick={handleOpenMenu}>
        <Tooltip title="Acties" placement="right" arrow>
          <Icon>more_vert</Icon>
        </Tooltip>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {showEigenPending && (
          <MenuItem onClick={() => onClickUpdateStatus('accepted')}>
            <ListItemIcon>
              <Icon>check</Icon>
            </ListItemIcon>
            Accepteer uitnodiging
          </MenuItem>
        )}

        {showEigenPending && (
          <MenuItem onClick={() => onClickUpdateStatus('declined')}>
            <ListItemIcon>
              <Icon>close</Icon>
            </ListItemIcon>
            Weiger uitnodiging
          </MenuItem>
        )}

        {showEigenAccepted && (
          <MenuItem onClick={() => onClickUpdateStatus('left')}>
            <ListItemIcon>
              <Icon>block</Icon>
            </ListItemIcon>
            Stop deelname
          </MenuItem>
        )}

        {showCancel && (
          <MenuItem onClick={() => onClickUpdateStatus('cancelled')}>
            <ListItemIcon>
              <Icon>cancel_schedule_send</Icon>
            </ListItemIcon>
            Annuleer uitnodiging
          </MenuItem>
        )}
      </Menu>

      {pendingAction?.status === 'accepted' && heeftUitgenodigdePlannen ? (
        <DialogConfirmSelectable
          open={open.confirm}
          title={pendingAction?.confirmTitle || 'Bevestigen'}
          contentText={`${pendingAction?.confirmContent || 'Weet je zeker dat je deze actie wilt uitvoeren?'}${heeftUitgenodigdePlannen ? ' Er zijn ook uitgenodigde plannen die kun je ook direct accepteren.' : ''}`}
          confirmButtonText={pendingAction?.confirmButtonText || 'Ja dat weet ik zeker'}
          selectableItems={uitgenodigdePlanItems}
          selectionTitle="Uitgenodigde plannen"
          selectAllLabel="Alle plannen accepteren"
          onClose={() => {
            closeDialog('confirm');
            setTimeout(() => {
              setPendingAction(null);
            }, 100);
          }}
          onConfirm={handleConfirm}
        />
      ) : (
        <DialogConfirm
          open={open.confirm}
          title={pendingAction?.confirmTitle || 'Bevestigen'}
          contentText={pendingAction?.confirmContent || 'Weet je zeker dat je deze actie wilt uitvoeren?'}
          confirmButtonText={pendingAction?.confirmButtonText || 'Ja dat weet ik zeker'}
          onClose={() => {
            closeDialog('confirm');
            setTimeout(() => {
              setPendingAction(null);
            }, 100);
          }}
          onConfirm={() => handleConfirm()}
        />
      )}
    </>
  );
};

export default OrganisatiesActionMenu;
